import { db, auth } from './firebase';
import { doc, getDoc, setDoc, updateDoc, writeBatch } from 'firebase/firestore';
import unifiedData from './data';
import dataScience from './data/dataScience';
import dataDivertissement from './data/dataDivertissement';
import dataSport from './data/dataSport';
import NobelPrize from './data/dataNobelPrize';
import dataLiterature from './data/dataLiterature';
import dataArt from './data/dataArt';
import dataGeography from './data/dataGeography';
import { normalizeTheme, normalizeThemeForComparison, getFlashcardScore, calculateStudiedCountToday, calculateTotalStudiedCount } from './utils';
import { themes } from './constants';
import { defaultLearnScore, defaultStats, defaultUserData } from './defaults';

const allData = {
  ...unifiedData,
  ...dataScience,
  ...dataDivertissement,
  ...dataSport,
  ...dataLiterature,
  ...dataArt,
  ...NobelPrize,
  ...dataGeography,
};

// Fonction pour déterminer la sous-collection learndata en fonction de l'ID
const getLearnDataCollectionName = (id) => {
  const numId = parseInt(id.replace('id', '')); // Extrait le numéro de "id1", "id2", etc.
  if (numId < 2000) return 'learndata0-2000';
  if (numId < 4000) return 'learndata2000-4000';
  if (numId < 6000) return 'learndata4000-6000';
  if (numId < 8000) return 'learndata6000-8000';
  if (numId < 10000) return 'learndata8000-10000';
  if (numId < 12000) return 'learndata10000-12000';
  if (numId < 14000) return 'learndata12000-14000';
  if (numId < 16000) return 'learndata14000-16000';
  if (numId < 18000) return 'learndata16000-18000';
  if (numId < 10000) return 'learndata18000-20000';
  return 'learndata20000-22000';
};

export const fetchLearnData = async (user, setUserData, setLearnData) => {
  try {
    console.log('Début de fetchLearnData');
    if (!user || !user.uid) {
      throw new Error('Utilisateur non défini ou UID manquant');
    }
    console.log('User UID (fetchLearnData):', user.uid);

    // Vérification de l'état d'authentification
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('Aucun utilisateur authentifié');
    }
    console.log('Utilisateur authentifié:', currentUser.uid);

    // Vérifier les données en cache
    const cachedData = localStorage.getItem(`learnData_${user.uid}`);
    if (cachedData) {
      const { userData: cachedUserData, learnData: cachedLearnData } = JSON.parse(cachedData);
      const hasValidLearnScore = cachedUserData.learnScore && themes.every(theme => cachedUserData.learnScore[normalizeTheme(theme)] !== undefined);
      const hasValidStats = cachedUserData.stats && typeof cachedUserData.stats.flashcardsStudied === 'number';
      if (
        cachedUserData.sessionHistory &&
        cachedUserData.sessionHistory.length > 0 &&
        hasValidLearnScore &&
        hasValidStats
      ) {
        console.log('Données trouvées dans localStorage avec sessionHistory, learnScore et stats valides, utilisation du cache');
        setUserData(cachedUserData);
        setLearnData(cachedLearnData);
        return;
      } else {
        console.warn('Données en cache invalides ou incomplètes (learnScore ou stats manquant ou incomplet), récupération depuis Firestore');
      }
    }

    // Récupérer userData depuis Firestore
    console.log('Récupération de userDocRef');
    const userDocRef = doc(db, 'users', user.uid);
    console.log('Appel à getDoc pour user');
    const userDocSnap = await getDoc(userDocRef);
    console.log('userDocSnap reçu:', userDocSnap.exists());

    let userData = defaultUserData;
    if (userDocSnap.exists()) {
      const data = userDocSnap.data();
      userData = {
        ...defaultUserData,
        activity: data.activity || defaultUserData.activity,
        dailyFlashcards: data.dailyFlashcards || defaultUserData.dailyFlashcards,
        dailySessions: data.dailySessions || defaultUserData.dailySessions,
        totalSessionsByTheme: data.totalSessionsByTheme || defaultUserData.totalSessionsByTheme,
        totalFlashcardsByTheme: data.totalFlashcardsByTheme || defaultUserData.totalFlashcardsByTheme,
        sessionHistory: data.sessionHistory || defaultUserData.sessionHistory,
        learnScore: { ...defaultLearnScore, ...data.learnScore },
        stats: { ...defaultStats, ...data.stats },
        subscription: data.subscription || { status: 'not_subscribed', willEndAt: null },
      };
    }

    // Liste des sous-collections
    const collections = [
      'learndata0-2000',
      'learndata2000-4000',
      'learndata4000-6000',
      'learndata6000-8000',
      'learndata8000-10000',
      'learndata10000-12000',
      'learndata12000-14000',
      'learndata14000-16000',
      'learndata16000-18000',
      'learndata18000-20000',
      'learndata20000-22000',
    ];

    // Charger learnData
    let learnData = {};
    console.log('Récupération des sous-collections learndata');
    for (const collName of collections) {
      console.log(`Tentative de lecture de la sous-collection: users/${user.uid}/${collName}`);
      const docRef = doc(db, 'users', user.uid, collName, 'flashcards');
      console.log(`Appel à getDoc pour users/${user.uid}/${collName}/flashcards`);
      const docSnap = await getDoc(docRef);
      console.log(`Résultat pour ${collName}: exists = ${docSnap.exists()}`);
      if (docSnap.exists()) {
        const data = docSnap.data();
        console.log(`Données récupérées pour ${collName}, nombre de flashcards: ${Object.keys(data.flashcards || {}).length}`);
        Object.assign(learnData, data.flashcards || {});
      }
    }
    console.log('learnData chargé, nombre total de flashcards:', Object.keys(learnData).length);

    // Vérifier et ajouter les flashcards manquantes
    console.log('Vérification des flashcards manquantes');
    const batch = writeBatch(db);
    const missingFlashcardsByCollection = {
      'learndata0-2000': {},
      'learndata2000-4000': {},
      'learndata4000-6000': {},
      'learndata6000-8000': {},
      'learndata8000-10000': {},
      'learndata10000-12000': {},
      'learndata12000-14000': {},
      'learndata14000-16000': {},
      'learndata16000-18000': {},
      'learndata18000-20000': {},
      'learndata20000-22000': {},
    };

    let hasMissingFlashcards = false;
    Object.keys(allData).forEach((id) => {
      if (!learnData[id]) {
        const collName = getLearnDataCollectionName(id);
        missingFlashcardsByCollection[collName][id] = {
          id,
          box: '1',
          doneToday: null,
          themes: allData[id].themes || ['Divers'],
        };
        learnData[id] = {
          box: '1',
          doneToday: null,
          themes: allData[id].themes || ['Divers'],
        };
        hasMissingFlashcards = true;
      } else if (!Array.isArray(learnData[id].themes)) {
        learnData[id].themes = allData[id].themes || ['Divers'];
      }
    });

    if (hasMissingFlashcards) {
      console.log('Écriture des flashcards manquantes dans Firestore');
      for (const [collName, flashcards] of Object.entries(missingFlashcardsByCollection)) {
        if (Object.keys(flashcards).length > 0) {
          const docRef = doc(db, 'users', user.uid, collName, 'flashcards');
          console.log(`Préparation de l'écriture pour users/${user.uid}/${collName}/flashcards, nombre de flashcards: ${Object.keys(flashcards).length}`);
          batch.set(docRef, { flashcards }, { merge: true });
          console.log(`Ajout de ${Object.keys(flashcards).length} flashcards dans ${collName} via batch`);
        }
      }
      console.log('Exécution de batch.commit');
      await batch.commit();
      console.log('Batch commit terminé');
    } else {
      console.log('Aucune flashcard manquante détectée');
    }

    // Mise à jour de l’état
    setUserData(userData);
    setLearnData(learnData);
    console.log('État mis à jour');

    // Sauvegarde dans localStorage
    localStorage.setItem(`learnData_${user.uid}`, JSON.stringify({ userData, learnData }));
    console.log('Fin de fetchLearnData');
  } catch (error) {
    console.error('Erreur lors de la récupération des données:', error.message, error.stack);
    setUserData(defaultUserData);
    setLearnData({});
    localStorage.setItem(`learnData_${user.uid}`, JSON.stringify({ userData: defaultUserData, learnData: {} }));
    throw error;
  }
};

export const startLearningSession = (theme, setSelectedTheme, setCurrentSession, setSessionCompleted, setSessionStartTime, selectWeightedItems, learnData) => {
  let normalizedTheme = normalizeTheme(theme);
  let selectedSubTheme = null;
  if (theme.includes('-start')) {
    const parts = theme.split('-');
    selectedSubTheme = parts[0];
    normalizedTheme = normalizeTheme(parts[1] || parts[0]);
  }

  const themeFlashcards = Object.keys(allData).filter((id) =>
    allData[id].themes.map(normalizeThemeForComparison).includes(normalizeThemeForComparison(normalizedTheme))
  );

  let filteredItems = themeFlashcards;
  if (selectedSubTheme) {
    filteredItems = themeFlashcards.filter((id) =>
      allData[id].themes.map(normalizeThemeForComparison).includes(normalizeThemeForComparison(selectedSubTheme))
    );
  }

  const selectedItems = selectWeightedItems(filteredItems, learnData);
  if (selectedItems.length === 0) {
    console.warn(`Aucune carte disponible pour démarrer une session sur ${normalizedTheme}${selectedSubTheme ? ` (${selectedSubTheme})` : ''}`);
    return;
  }

  setSelectedTheme(selectedSubTheme || normalizedTheme);
  setCurrentSession({
    items: selectedItems,
    currentIndex: 0,
    currentCard: allData[selectedItems[0]] || null,
    isCardLocked: false,
    answers: [],
    feedback: null,
  });
  setSessionCompleted(false);
  setSessionStartTime(Date.now());
};

export const handleAnswer = async (isCorrect, currentSession, setCurrentSession, user, selectedTheme, learnData, setLearnData, userData, setUserData) => {
  if (currentSession.isCardLocked || !currentSession.items.length) return;

  try {
    console.log('User UID (handleAnswer):', user.uid);

    const currentCardId = currentSession.items[currentSession.currentIndex];
    const today = new Date().toISOString().split('T')[0];
    const cardAlreadyAnsweredToday = learnData[currentCardId]?.doneToday === today;

    const currentBox = parseInt(learnData[currentCardId]?.box || '1');
    const newBox = cardAlreadyAnsweredToday ? currentBox : (isCorrect ? Math.min(5, currentBox + 1) : Math.max(1, currentBox - 1));
    const cardThemes = allData[currentCardId]?.themes || [normalizeTheme(selectedTheme)];

    // Get the collection name and document reference
    const collName = getLearnDataCollectionName(currentCardId);
    const learnDocRef = doc(db, 'users', user.uid, collName, 'flashcards');

    // Check if the document exists
    const docSnap = await getDoc(learnDocRef);
    if (!docSnap.exists()) {
      console.log(`Document ${collName}/flashcards does not exist, creating it.`);
      await setDoc(learnDocRef, { flashcards: {} }, { merge: true });
    }

    // Update the document
    console.log(`Mise à jour de users/${user.uid}/${collName}/flashcards pour la flashcard ${currentCardId}`);
    await updateDoc(learnDocRef, {
      [`flashcards.${currentCardId}`]: {
        id: currentCardId,
        box: newBox.toString(),
        doneToday: today,
        themes: cardThemes,
      },
    });

    // Update local state
    setLearnData((prev) => ({
      ...prev,
      [currentCardId]: {
        ...prev[currentCardId],
        box: newBox.toString(),
        doneToday: today,
        themes: cardThemes,
      },
    }));

    // Update user data
    setUserData((prev) => ({
      ...prev,
      stats: {
        ...prev.stats,
        flashcardsStudied: (prev.stats.flashcardsStudied || 0) + 1,
        currentStreak: calculateStreak(prev.stats, today),
        lastStudyDate: today,
        badges: prev.stats.badges.includes('100_flashcards') || (prev.stats.flashcardsStudied || 0) + 1 < 100 ? prev.stats.badges : [...prev.stats.badges, '100_flashcards'],
      },
      activity: {
        daily: {
          ...prev.activity.daily,
          [today]: {
            ...prev.activity.daily[today],
            flashcards: (prev.activity.daily[today]?.flashcards || 0) + 1,
          },
        },
        monthly: {
          ...prev.activity.monthly,
          [today.slice(0, 7)]: {
            ...prev.activity.monthly[today.slice(0, 7)],
            flashcards: (prev.activity.monthly[today.slice(0, 7)]?.flashcards || 0) + 1,
          },
        },
      },
      dailyFlashcards: {
        ...prev.dailyFlashcards,
        [today]: {
          ...prev.dailyFlashcards[today],
          [normalizeTheme(selectedTheme)]: (prev.dailyFlashcards[today]?.[normalizeTheme(selectedTheme)] || 0) + 1,
        },
      },
      totalFlashcardsByTheme: {
        ...prev.totalFlashcardsByTheme,
        [normalizeTheme(selectedTheme)]: (prev.totalFlashcardsByTheme[normalizeTheme(selectedTheme)] || 0) + 1,
      },
    }));

    setCurrentSession((prev) => ({
      ...prev,
      isCardLocked: true,
      feedback: { isCorrect, message: isCorrect ? '' : '' },
    }));
  } catch (error) {
    console.error('Erreur lors de la gestion de la réponse:', error.message, error.stack);
  }
};

export const moveToNextCard = async (
  currentSession,
  setCurrentSession,
  setSessionCompleted,
  userData,
  setUserData,
  user,
  selectedTheme,
  sessionStartTime,
  learnData,
  setLearnData
) => {
  const nextIndex = currentSession.currentIndex + 1;
  const today = new Date().toISOString().split('T')[0];
  const monthKey = today.slice(0, 7);

  if (nextIndex < currentSession.items.length) {
    setCurrentSession((prev) => ({
      ...prev,
      currentIndex: nextIndex,
      currentCard: allData[prev.items[nextIndex]],
      isCardLocked: false,
      feedback: null,
      answers: [],
    }));
  } else {
    try {
      console.log('User UID (moveToNextCard):', user.uid);

      const normalizedTheme = normalizeTheme(selectedTheme);
      const timeSpent = Math.round((Date.now() - sessionStartTime) / 60000);

      const studiedCountToday = calculateStudiedCountToday(normalizedTheme, learnData);
      const totalStudiedCount = calculateTotalStudiedCount(normalizedTheme, learnData);

      const allThemes = new Set();
      Object.values(learnData).forEach((card) => {
        (card.themes || []).forEach((theme) => allThemes.add(normalizeTheme(theme)));
      });

      const updatedLearnScore = {};
      allThemes.forEach((theme) => {
        const themeCards = Object.entries(learnData)
          .filter(([_, card]) =>
            card.themes &&
            card.themes
              .map(normalizeThemeForComparison)
              .includes(normalizeThemeForComparison(theme))
          )
          .map(([_, card]) => card);

        const totalScore = themeCards.reduce(
          (sum, card) => sum + getFlashcardScore(card.box),
          0
        );
        const themeScore = themeCards.length > 0 ? totalScore / themeCards.length : 0;
        updatedLearnScore[normalizeTheme(theme)] = themeScore;
      });

      // Récupérer les données actuelles de Firestore pour préserver subscription
      const userDocRef = doc(db, 'users', user.uid);
      const userDocSnap = await getDoc(userDocRef);
      const currentFirestoreData = userDocSnap.exists() ? userDocSnap.data() : {};

      // Fusionner les données utilisateur tout en préservant la subscription existante
      const updatedUserData = {
        ...userData,
        ...currentFirestoreData, // Priorité aux données actuelles de Firestore
        activity: {
          daily: {
            ...userData.activity.daily,
            [today]: {
              ...userData.activity.daily[today],
              timeSpent: (userData.activity.daily[today]?.timeSpent || 0) + timeSpent,
              flashcards: studiedCountToday,
            },
          },
          monthly: {
            ...userData.activity.monthly,
            [monthKey]: {
              ...userData.activity.monthly[monthKey],
              timeSpent: (userData.activity.monthly[monthKey]?.timeSpent || 0) + timeSpent,
              flashcards: studiedCountToday,
            },
          },
        },
        dailyFlashcards: {
          ...userData.dailyFlashcards,
          [today]: {
            ...userData.dailyFlashcards[today],
            [normalizedTheme]: studiedCountToday,
          },
        },
        totalFlashcardsByTheme: {
          ...userData.totalFlashcardsByTheme,
          [normalizedTheme]: totalStudiedCount,
        },
        dailySessions: {
          ...userData.dailySessions,
          [today]: {
            ...userData.dailySessions[today],
            [normalizedTheme]: (userData.dailySessions[today]?.[normalizedTheme] || 0) + 1,
          },
        },
        totalSessionsByTheme: {
          ...userData.totalSessionsByTheme,
          [normalizedTheme]: (userData.totalSessionsByTheme[normalizedTheme] || 0) + 1,
        },
        sessionHistory: [
          ...(userData.sessionHistory || []),
          {
            date: new Date().toISOString(),
            category: normalizedTheme,
            flashcardsStudied: currentSession.items.length,
            timeSpent: timeSpent,
            score: updatedLearnScore[normalizedTheme] || 0,
          },
        ],
        learnScore: updatedLearnScore,
        stats: {
          ...userData.stats,
          flashcardsStudied: (userData.stats.flashcardsStudied || 0) + currentSession.items.length,
          currentStreak: calculateStreak(userData.stats, today),
          lastStudyDate: today,
          badges: userData.stats.badges || [],
        },
        subscription: currentFirestoreData.subscription || userData.subscription || { status: 'not_subscribed', willEndAt: null }, // Préserver subscription
      };

      console.log('[moveToNextCard] Données mises à jour avec subscription:', updatedUserData.subscription);

      setUserData(updatedUserData);

      console.log('Mise à jour de users/{userId} avec setDoc');
      await setDoc(userDocRef, updatedUserData, { merge: true });

      localStorage.setItem(
        `learnData_${user.uid}`,
        JSON.stringify({
          userData: updatedUserData,
          learnData,
        })
      );

      setCurrentSession((prev) => ({ ...prev, currentCard: null }));
      setSessionCompleted(true);
    } catch (error) {
      console.error('Erreur lors de la finalisation de la session:', error.message, error.stack);
      setCurrentSession((prev) => ({
        ...prev,
        feedback: { isCorrect: false, message: `Erreur lors de la sauvegarde de la session : ${error.message}` },
      }));
    }
  }
};

const calculateStreak = (stats, today) => {
  const lastStudyDate = stats.lastStudyDate || '';
  return lastStudyDate === getYesterday(today) ? (stats.currentStreak || 0) + 1 : 1;
};

const getYesterday = (date) => {
  const d = new Date(date);
  d.setDate(d.getDate() - 1);
  return d.toISOString().split('T')[0];
};